import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Switch, TextField, Typography } from "@mui/material";
import { fetchCertificateTypes, saveCertificateType } from "../../api/apiClient";

// Every certificate type the platform supports. A center sets up the ones it issues.
export const CERTIFICATE_TYPES = [
  {
    type: "DEFAULT",
    name: "Certificate of completion",
    description: "The platform's own certificate, with a PDF on the student's record. Added to every new class.",
  },
  {
    type: "AHA",
    name: "AHA eCards",
    description: "American Heart Association eCards, sent to AHA for students who pass an AHA class. Instructors need an AHA instructor ID, set in Instructor Management.",
  },
];

// Standard status toggle: green when on, red when off.
const STATUS_SWITCH_SX = {
  "& .MuiSwitch-switchBase": { color: "error.main" },
  "& .MuiSwitch-switchBase + .MuiSwitch-track": { backgroundColor: "error.main", opacity: 0.5 },
  "& .MuiSwitch-switchBase.Mui-checked": { color: "success.main" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "success.main", opacity: 0.5 },
};

const CENTER_ID =/^[A-Za-z0-9]{1,10}$/;
// AHA lmsId: 1-50 visible characters, no spaces (matches the backend rule).
const LMS_ID = /^[\x21-\x7E]{1,50}$/;
const parseSiteIds = (text) => text.split(",").map((s) => s.trim()).filter(Boolean);

function ahaSettingsError({ tcId, siteIds, lmsId }) {
  if (tcId.trim() && !CENTER_ID.test(tcId.trim())) return "The AHA Training Center ID is 1–10 letters or digits.";
  const sites = parseSiteIds(siteIds);
  if (sites.some((s) => !CENTER_ID.test(s))) return "Each Training Site ID is 1–10 letters or digits.";
  if (new Set(sites).size !== sites.length) return "A Training Site ID is listed twice.";
  if (lmsId.trim() && !LMS_ID.test(lmsId.trim())) return "The LMS ID is 1–50 characters with no spaces.";
  return null;
}

function AhaSettingsForm({ settings, disabled, onSave }) {
  const [tcId, setTcId] = useState(settings?.tcId || "");
  const [siteIds, setSiteIds] = useState((settings?.trainingSiteIds || []).join(", "));
  const [lmsId, setLmsId] = useState(settings?.lmsId || "");
  const error = ahaSettingsError({ tcId, siteIds, lmsId });
  return (
    <Box sx={{ mt: 1.5 }}>
      <TextField label="AHA Training Center ID" value={tcId} onChange={(e) => setTcId(e.target.value)} disabled={disabled}
        fullWidth margin="dense" inputProps={{ maxLength: 10 }}
        helperText="The Training Center ID AHA gave your center (for example NJ00569). Sent with every eCard request." />
      <TextField label="AHA Training Site IDs" value={siteIds} onChange={(e) => setSiteIds(e.target.value)} disabled={disabled}
        fullWidth margin="dense" helperText="Optional. Training Sites aligned with your center, separated by commas." />
      <TextField label="LMS ID" value={lmsId} onChange={(e) => setLmsId(e.target.value)} disabled={disabled}
        fullWidth margin="dense" inputProps={{ maxLength: 50 }}
        helperText="The LMS ID your center received from Lifeforce. Sent with every eCard request." />
      {error && <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>{error}</Typography>}
      <Button variant="contained" size="small" sx={{ mt: 1 }} disabled={disabled || !!error}
        onClick={() => onSave({ tcId: tcId.trim(), trainingSiteIds: parseSiteIds(siteIds), lmsId: lmsId.trim() })}>
        Save AHA settings
      </Button>
    </Box>
  );
}

/**
 * Certificate setup for the training center. Admins turn certificate types on and fill in their settings;
 * coordinators see which types are set up, to add them to classes.
 */
export default function CertificateSetupPanel({ canEdit }) {
  const [types, setTypes] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    fetchCertificateTypes()
      .then((data) => { if (active) setTypes(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setError("Certificate setup couldn't be loaded."); });
    return () => { active = false; };
  }, []);

  const save = async (type, changes) => {
    setSaving(type);
    setMessage(null);
    try {
      const saved = await saveCertificateType(type, changes);
      setTypes((current) => [...(current || []).filter((t) => t.type !== type), saved]
        .sort((a, b) => CERTIFICATE_TYPES.findIndex((c) => c.type === a.type) - CERTIFICATE_TYPES.findIndex((c) => c.type === b.type)));
      setMessage({ severity: "success", text: `${saved.name} saved.` });
    } catch (e) {
      setMessage({ severity: "error", text: e?.response?.data?.message || "That change couldn't be saved." });
    } finally {
      setSaving(null);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (types === null) return <Typography>Loading certificate setup…</Typography>;

  return (
    <Box data-testid="certificate-setup" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>Certificates</Typography>
        <Typography variant="body2" color="text.secondary">
          The certificates your center can issue. Coordinators add them to classes; the instructor sends them all
          when the class is complete.
          {!canEdit && " Only an administrator can change this setup."}
        </Typography>
      </Box>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {CERTIFICATE_TYPES.map((known) => {
        const current = types.find((t) => t.type === known.type);
        const enabled = !!current?.enabled;
        return (
          <Box key={known.type} data-testid={`certificate-type-${known.type}`}
            sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 700 }}>{known.name}</Typography>
              <Chip size="small" label={enabled ? "On" : current ? "Off" : "Not set up"} color={enabled ? "success" : "default"} />
              <Box sx={{ flexGrow: 1 }} />
              {canEdit && (
                <Switch checked={enabled} disabled={saving === known.type}
                  onChange={(e) => save(known.type, { enabled: e.target.checked })}
                  inputProps={{ "aria-label": `${known.name} on` }} sx={STATUS_SWITCH_SX} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{known.description}</Typography>
            {known.type === "AHA" && canEdit && current && (
              <AhaSettingsForm key={JSON.stringify(current.settings || {})} settings={current.settings}
                disabled={saving === "AHA"} onSave={(settings) => save("AHA", { settings })} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
